import React, { useState } from "react";
import Card from "components/card";
import { useLanguage } from "context/LanguageContext";
import { authService } from "services/authService";
import { userPortalService } from "services/userPortalService";
import { MdVisibility, MdVisibilityOff, MdLock } from "react-icons/md";
import ActionButton from "components/ui/ActionButton";

const inputClass =
  "rounded-lg border border-line bg-transparent px-4 py-2.5 text-sm text-ink outline-none placeholder:text-ink-subtle focus:border-brand-500 w-full pr-10";

const labelClass =
  "text-sm font-medium text-ink-muted";

const ChangePassword = () => {
  const { t } = useLanguage();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const validate = (): string | null => {
    if (!currentPassword.trim()) {
      return t("changePassword.errorCurrentPasswordRequired");
    }
    if (newPassword.length < 8) {
      return t("changePassword.errorPasswordTooShort");
    }
    if (newPassword !== confirmPassword) {
      return t("changePassword.errorPasswordMismatch");
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const user = authService.getStoredUser();
    if (!user) {
      setError("User not found");
      return;
    }

    setSaving(true);
    try {
      await userPortalService.changePassword(user.id, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess(t("changePassword.success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Change password failed");
    } finally {
      setSaving(false);
    }
  };

  const renderPasswordField = (
    label: string,
    placeholder: string,
    value: string,
    onChange: (v: string) => void,
    show: boolean,
    onToggle: () => void,
  ) => (
    <div className="flex flex-col gap-1.5">
      <label className={labelClass}>{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
        <ActionButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 !p-1 text-ink-subtle hover:text-navy-700 dark:hover:text-white"
          tabIndex={-1}
        >
          {show ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
        </ActionButton>
      </div>
    </div>
  );

  return (
    <div className="mt-5 flex flex-col gap-5">
      <h2 className="text-2xl font-bold text-ink">
        {t("changePassword.title")}
      </h2>

      {success && (
        <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-500/10 dark:text-green-300">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <Card extra="p-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div className="flex items-center gap-3 pb-2 border-b border-line">
            <MdLock className="h-5 w-5 text-brand-500" />
            <span className="text-sm font-semibold text-ink">
              {t("changePassword.title")}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {renderPasswordField(
              t("changePassword.currentPassword"),
              t("changePassword.currentPasswordPlaceholder"),
              currentPassword,
              setCurrentPassword,
              showCurrent,
              () => setShowCurrent((v) => !v),
            )}
            {renderPasswordField(
              t("changePassword.newPassword"),
              t("changePassword.newPasswordPlaceholder"),
              newPassword,
              setNewPassword,
              showNew,
              () => setShowNew((v) => !v),
            )}
            {renderPasswordField(
              t("changePassword.confirmPassword"),
              t("changePassword.confirmPasswordPlaceholder"),
              confirmPassword,
              setConfirmPassword,
              showConfirm,
              () => setShowConfirm((v) => !v),
            )}
          </div>

          <div className="flex justify-end pt-2">
            <ActionButton type="submit" variant="primary" disabled={saving}>
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t("changePassword.submit")}
                </span>
              ) : (
                t("changePassword.submit")
              )}
            </ActionButton>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ChangePassword;
